import React, { useState, useEffect } from "react";
import stylez from "./NewProduct.module.css";
import SuccessDialogue from "../../../Components/Shared/SuccessDialog/SuccessDialog";
import axiosInstance from "../../../Configuration/axiosInstance";
import LoadingSpinner from "../../../Components/Shared/Loader/Loader";

const AddNewProducts = () => {
  const [product, setProduct] = useState({
    productName: "",
    productId: "",
    sku: "",
    category: "",
    price: 0,
    gst: 10,
    productDescription: "",
    imageUrl: "",
    weight: 0,
    // ✅ NEW: Product relationship fields
    productType: "individual",
    parentProduct: null,
    multiplier: 1,
  });

  // ✅ NEW: Available individual products for parent selection
  const [availableParentProducts, setAvailableParentProducts] = useState([]);
  const [msg, setMsg] = useState();
  const [dialogBox, setDialogBox] = useState(false);
  const [loading, setLoading] = useState(false);

  // ✅ NEW: Fetch individual products for parent selection
  useEffect(() => {
    axiosInstance
      .get("products/allproducts?productType=individual")
      .then((response) => {
        setAvailableParentProducts(response.data.data || []);
      })
      .catch((error) => {
        console.error("Error fetching parent products:", error);
      });
  }, []);

  const resetForm = () => {
    setProduct({
      productName: "",
      productId: "",
      sku: "",
      category: "",
      price: 0,
      gst: 10,
      productDescription: "",
      imageUrl: "",
      weight: 0,
      // ✅ NEW: Reset relationship fields
      productType: "individual",
      parentProduct: null,
      multiplier: 1,
    });
  };

  const handleInput = (e) => {
    const { name, value } = e.target;

    setProduct((prevValue) => {
      const updated = {
        ...prevValue,
        [name]: value,
      };

      // ✅ NEW: Auto-reset parent and multiplier when switching to individual
      if (name === "productType" && value === "individual") {
        updated.parentProduct = null;
        updated.multiplier = 1;
      }

      // ✅ NEW: Set default multiplier when switching to bulk
      if (
        name === "productType" &&
        value === "bulk" &&
        updated.multiplier === 1
      ) {
        updated.multiplier = 10; // Default bulk multiplier
      }

      return updated;
    });
  };

  const handleSubmit = () => {
    setLoading(true);

    // ✅ NEW: Enhanced validation including product relationships
    if (
      !product.productName ||
      !product.productId ||
      !product.sku ||
      !product.price ||
      !product.productDescription ||
      !product.weight
    ) {
      setLoading(false);
      setMsg("Please fill in all required fields ❗");
      setDialogBox(true);
      setTimeout(() => setDialogBox(false), 2000);
      return;
    }

    // ✅ NEW: Validate bulk product requirements
    if (product.productType === "bulk") {
      if (!product.parentProduct) {
        setLoading(false);
        setMsg("Please select a parent product for bulk items ❗");
        setDialogBox(true);
        setTimeout(() => setDialogBox(false), 2000);
        return;
      }
      if (!product.multiplier || product.multiplier <= 0) {
        setLoading(false);
        setMsg("Please enter a valid multiplier for bulk items ❗");
        setDialogBox(true);
        setTimeout(() => setDialogBox(false), 2000);
        return;
      }
    }

    setMsg("Creating a New Product....");
    axiosInstance.post(`products`, product).then((data) => {
      setLoading(false);
      setMsg(data.data.message);
      setDialogBox(true);
      resetForm();
    });
  };

  const handleClose = () => {
    setDialogBox(false);
    setMsg("");
  };

  // ✅ NEW: Get selected parent product details
  const selectedParentProduct = availableParentProducts.find(
    (p) => p._id === product.parentProduct
  );

  return (
    <>
      <h2>Add New Products</h2>
      <div className={stylez.box}>
        <SuccessDialogue show={dialogBox} handleClose={handleClose} msg={msg} />
        {loading ? (
          <LoadingSpinner />
        ) : (
          <>
            <div className={stylez.newContainer}>
              <div className={stylez.divs}>
                <label>Product Name: </label>
                <input
                  required
                  name="productName"
                  type="text"
                  placeholder="Firebrand BBQ Pellets 10kg"
                  value={product.productName}
                  onChange={handleInput}
                />
                <label>Product ID:</label>
                <input
                  required
                  name="productId"
                  type="number"
                  placeholder="10421"
                  value={product.productId}
                  onChange={handleInput}
                />
                <label>SKU: </label>
                <input
                  required
                  name="sku"
                  type="text"
                  placeholder="FBPEL10KG"
                  value={product.sku}
                  onChange={handleInput}
                />

                {/* ✅ NEW: Product Type Selection */}
                <label>Product Type *</label>
                <select
                  required
                  name="productType"
                  value={product.productType}
                  onChange={handleInput}
                  className={stylez.productTypeSelect}
                >
                  <option value="individual">Individual Product</option>
                  <option value="bulk">Bulk Product</option>
                </select>
              </div>

              <div className={stylez.divs}>
                <label>Category</label>
                <select
                  required
                  name="category"
                  value={product.category}
                  onChange={handleInput}
                >
                  <option value="">Select an Option</option>
                  <option value="Fuel">Fuel</option>
                  <option value="Accessories">Accessories</option>
                </select>

                <label>WholeSale Price</label>
                <input
                  required
                  name="price"
                  type="number"
                  step="0.01"
                  placeholder="25.09"
                  value={product.price}
                  onChange={handleInput}
                />

                <label>Weight (kg) *</label>
                <input
                  required
                  name="weight"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="10.0"
                  value={product.weight}
                  onChange={handleInput}
                  className={stylez.weightInput}
                />

                <label>GST (% Percentage)</label>
                <input
                  name="gst"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  placeholder="10.00"
                  value={product.gst}
                  onChange={handleInput}
                />
              </div>

              <div className={stylez.divs}>
                {/* ✅ NEW: Bulk Product Fields */}
                {product.productType === "bulk" && (
                  <>
                    <label>Parent Product *</label>
                    <select
                      required
                      name="parentProduct"
                      value={product.parentProduct || ""}
                      onChange={handleInput}
                      className={stylez.parentProductSelect}
                    >
                      <option value="">Select Parent Product</option>
                      {availableParentProducts.map((parentProd) => (
                        <option key={parentProd._id} value={parentProd._id}>
                          {parentProd.productName} ({parentProd.sku})
                        </option>
                      ))}
                    </select>

                    <label>Multiplier *</label>
                    <input
                      required
                      name="multiplier"
                      type="number"
                      min="1"
                      placeholder="10"
                      value={product.multiplier}
                      onChange={handleInput}
                      className={stylez.multiplierInput}
                    />

                    {/* ✅ NEW: Bulk Product Info */}
                    {selectedParentProduct && (
                      <div className={stylez.bulkInfo}>
                        <h4>🔗 Bulk Product Info:</h4>
                        <p>
                          <strong>Parent:</strong>{" "}
                          {selectedParentProduct.productName}
                        </p>
                        <p>
                          <strong>1 bulk unit</strong> ={" "}
                          <strong>{product.multiplier} individual units</strong>
                        </p>
                        <p>
                          <small>
                            Stock will be managed through the parent product
                          </small>
                        </p>
                      </div>
                    )}
                  </>
                )}

                <label>Product Description:</label>
                <textarea
                  required
                  name="productDescription"
                  type="text"
                  rows={product.productType === "bulk" ? 6 : 10}
                  style={{
                    height: product.productType === "bulk" ? "100px" : "133px",
                    width: "298px",
                  }}
                  placeholder="Smoked food has never been easier with our all-natural BBQ pellets..."
                  value={product.productDescription}
                  onChange={handleInput}
                />

                <label>Image URL</label>
                <input
                  name="imageUrl"
                  type="url"
                  placeholder="https://example.com/image.jpg"
                  value={product.imageUrl}
                  onChange={handleInput}
                />
              </div>
            </div>

            {/* ✅ NEW: Enhanced product preview with relationship info */}
            <div className={stylez.productPreview}>
              <h3 className={stylez.previewTitle}>Product Preview</h3>
              <div className={stylez.previewContent}>
                <div className={stylez.previewInfo}>
                  <p>
                    <strong>Name:</strong>{" "}
                    {product.productName || "Not specified"}
                  </p>
                  <p>
                    <strong>SKU:</strong> {product.sku || "Not specified"}
                  </p>
                  <p>
                    <strong>Category:</strong>{" "}
                    {product.category || "Not specified"}
                  </p>
                  <p>
                    <strong>Price:</strong> ${product.price || "0.00"}
                  </p>
                  <p>
                    <strong>Weight:</strong> {product.weight || "0"} kg
                  </p>
                  <p>
                    <strong>GST:</strong> {product.gst || "0"}%
                  </p>

                  {/* ✅ NEW: Product type info */}
                  <div className={stylez.productTypeInfo}>
                    <p>
                      <strong>Type:</strong>
                      <span
                        className={`${stylez.productTypeBadge} ${
                          stylez[product.productType]
                        }`}
                      >
                        {product.productType === "individual"
                          ? "🔵 Individual"
                          : "📦 Bulk"}
                      </span>
                    </p>

                    {product.productType === "bulk" && (
                      <>
                        {selectedParentProduct && (
                          <p>
                            <strong>Parent:</strong>{" "}
                            {selectedParentProduct.productName}
                          </p>
                        )}
                        <p>
                          <strong>Multiplier:</strong> {product.multiplier}x
                        </p>
                        <div className={stylez.bulkPreviewNote}>
                          <small>
                            💡 This bulk product will share stock with its
                            parent product
                          </small>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                {product.imageUrl && (
                  <div className={stylez.previewImage}>
                    <img
                      src={product.imageUrl}
                      alt="Product preview"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            <button className="confirmBtn" onClick={handleSubmit}>
              Add Product
            </button>
          </>
        )}
      </div>
    </>
  );
};

export default AddNewProducts;
